import {
	ParsedResourceInfo,
	ResourceType,
	Watcher,
	WatcherResourceResult,
} from "@/utils/watcher";
import { ref } from "vue";

export type ResourceSource = { url: string; anchor?: string };

export type ResourceResult = WatcherResourceResult & {
	sitemap: string;
	sources: ResourceSource[];
};

export const resourceWatcherService = (() => {
	const resourceUrlToIndexMap: Record<string, number> = {};
	const resourceResults = ref<ResourceResult[]>([]);
	// stores the sources of resources to avoid fetching same resource multiple times
	const pendingResourceRequests: Record<string, ResourceSource[]> = {};

	let _isResourceTypeWatched = {} as Record<ResourceType, boolean>;
	for (const resourceType of Object.keys(ResourceType)) {
		_isResourceTypeWatched[resourceType as ResourceType] = true;
	}
	// restore isResourceTypeWatched from localStorage
	_isResourceTypeWatched = {
		..._isResourceTypeWatched,
		...(JSON.parse(localStorage.getItem("isResourceTypeWatched") as any) ??
			{}),
	};
	const isResourceTypeWatched = ref(_isResourceTypeWatched);

	const setIsResourceTypeWatched = (type: ResourceType, value: boolean) => {
		isResourceTypeWatched.value[type] = value;
		localStorage.setItem(
			"isResourceTypeWatched",
			JSON.stringify(isResourceTypeWatched.value)
		);
	};

	const testResources = async (
		resources: ParsedResourceInfo[],
		sitemap: string,
		source: string,
		cb?: (result: ResourceResult) => void
	) => {
		for (const resource of resources) {
			if (!isResourceTypeWatched.value[resource.type]) {
				continue;
			}

			const resourceSourceObj: ResourceSource = {
				url: source,
				anchor: resource.anchor,
			};

			// check if this resource was already tested and append the url to the sources
			const resourceUrl = resource.url;
			if (resourceUrlToIndexMap[resourceUrl] !== undefined) {
				const index = resourceUrlToIndexMap[resourceUrl];
				// check if this source is already in resource's sources
				if (
					resourceResults.value[index].sources.findIndex(
						s => s.url === source
					) === -1
				) {
					resourceResults.value[index].sources.push(
						resourceSourceObj
					);
				}
				continue;
			}

			// check if there is a pending request for this resource
			if (resourceUrl in pendingResourceRequests) {
				pendingResourceRequests[resourceUrl].push(resourceSourceObj);
				continue;
			}

			// test resource and block parallel requests for this resource
			pendingResourceRequests[resourceUrl] = [];
			const watcherResult = await Watcher.testResource(resource);
			const otherSources = pendingResourceRequests[resourceUrl];
			delete pendingResourceRequests[resourceUrl];

			const result = {
				...watcherResult,
				sitemap,
				sources: [
					{ url: source, anchor: resource.anchor },
					...(otherSources || []),
				],
			};

			const newIndex = resourceResults.value.length;
			// insert result in the beginning
			resourceResults.value.push(result);
			resourceUrlToIndexMap[resourceUrl] = newIndex;

			cb?.(result);
		}
	};

	return {
		resourceResults,
		testResources,

		isResourceTypeWatched,
		setIsResourceTypeWatched,
	};
})();
